import { useCallback, useRef, useState } from 'react';
import { CAROPAERA_REDEEM_GHOST_DURATION_MS } from '../config/caropaeraRedeemGhostEffect';

/**
 * Caropaera War Hero (145): redeem_ghost.png on triggered Redeem units for 2s.
 */
export function useCaropaeraRedeemGhost() {
  const activeInstanceIdsRef = useRef(new Set());
  const timeoutIdsRef = useRef(new Map());
  const consumedGhostAnimationKeysRef = useRef(new Set());
  const [ghostTick, setGhostTick] = useState(0);

  const queueGhosts = useCallback((grants) => {
    if (!Array.isArray(grants) || grants.length === 0) {
      return;
    }

    grants.forEach((grant) => {
      if (!grant?.unitInstanceId) {
        throw new Error('useCaropaeraRedeemGhost.queueGhosts: unitInstanceId is required');
      }
      if (grant.key && consumedGhostAnimationKeysRef.current.has(grant.key)) {
        return;
      }
      if (grant.key) {
        consumedGhostAnimationKeysRef.current.add(grant.key);
      }
      const durationMs = Number(grant.durationMs) > 0
        ? Number(grant.durationMs)
        : CAROPAERA_REDEEM_GHOST_DURATION_MS;

      const priorTimeout = timeoutIdsRef.current.get(grant.unitInstanceId);
      if (priorTimeout != null) {
        window.clearTimeout(priorTimeout);
      }

      activeInstanceIdsRef.current.add(grant.unitInstanceId);
      setGhostTick((tick) => tick + 1);

      const timeoutId = window.setTimeout(() => {
        activeInstanceIdsRef.current.delete(grant.unitInstanceId);
        timeoutIdsRef.current.delete(grant.unitInstanceId);
        setGhostTick((tick) => tick + 1);
      }, durationMs);
      timeoutIdsRef.current.set(grant.unitInstanceId, timeoutId);
    });
  }, []);

  const isGhostActiveForUnit = useCallback((instanceId) => {
    void ghostTick;
    if (!instanceId) {
      return false;
    }
    return activeInstanceIdsRef.current.has(instanceId);
  }, [ghostTick]);

  const reset = useCallback(() => {
    for (const timeoutId of timeoutIdsRef.current.values()) {
      window.clearTimeout(timeoutId);
    }
    timeoutIdsRef.current.clear();
    activeInstanceIdsRef.current.clear();
    consumedGhostAnimationKeysRef.current.clear();
    setGhostTick((tick) => tick + 1);
  }, []);

  return {
    queueGhosts,
    isGhostActiveForUnit,
    reset,
  };
}

export default useCaropaeraRedeemGhost;
